import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { BaseSiteService, LanguageService, WindowRef } from '@spartacus/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { NEWS_LIST_ROW_COUNT } from './constants';
import { CarouselItem } from '@app/ui-components/carousel/types';

import { loadNewsAction, newsPageResizeAction, showMoreAction } from './store/actions';
import { newsListExpandableSelector, newsListMaxItemsSelector, newsListSelector } from './store/news-list.selectors';

@Component({
    selector: 'dp-news-list',
    templateUrl: './news-list.component.html',
    styleUrls: ['./news-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DpNewsListComponent implements OnInit, OnDestroy {
  newsList$: Observable<CarouselItem[]>;
  maxItems$: Observable<number>;
  expandable$: Observable<boolean>;
  destroy$: Subject<void> = new Subject<void>();

  readonly #store = inject(Store);
  readonly #windowRef = inject(WindowRef);
  readonly #languageService = inject(LanguageService);
  readonly #baseSiteService = inject(BaseSiteService);

  constructor() {
    this.newsList$ = this.#store.select(newsListSelector);
    this.maxItems$ = this.#store.select(newsListMaxItemsSelector);
    this.expandable$ = this.#store.select(newsListExpandableSelector);
  }

  ngOnInit(): void {
    this.#windowRef.resize$
      .pipe(
        takeUntil(this.destroy$),
        map((res) => (res?.target?.innerWidth > 1024 ? NEWS_LIST_ROW_COUNT.DESKTOP : NEWS_LIST_ROW_COUNT.MOBILE)),
        distinctUntilChanged()
      )
      .subscribe((rowCount: number) => this.#store.dispatch(newsPageResizeAction({ rowCount })));

    combineLatest([this.#baseSiteService.getActive(), this.#languageService.getActive()])
      .pipe(takeUntil(this.destroy$))
      .subscribe((_) => this.#store.dispatch(loadNewsAction()));
  }

  loadMore(): void {
    this.#store.dispatch(showMoreAction());
  }

  ngOnDestroy(): void {
    this.destroy$.next(void 0);
    this.destroy$.complete();
  }
}
